/**
 * PropiedadIntegrationPanel — panel "Integración PMS" del Modal de
 * propiedad. Extraído de propiedades.tsx en round 2026-04-27 (Fase 8).
 *
 * QUÉ HACE
 *   1. Input "ID propiedad corto" (ej. JB01J) — el identificador
 *      legible que Stays usa internamente para una propiedad.
 *   2. Botón "Obtener ID largo" — hace fetch a /stays/listing-long-id/
 *      con el corto y rellena el ID largo (ObjectId de MongoDB) que
 *      todos los endpoints PMS usan internamente.
 *   3. Input "ID propiedad largo" — read-only por default (modo
 *      sincronizado). Tag indica el modo. Candado lateral permite
 *      destrabar para edición manual (admin) en casos edge.
 *
 * POR QUÉ ESTÁ AQUÍ Y NO INLINE
 *   - El bloque tenía ~290 LOC mayoritariamente del onClick del botón
 *     "Obtener ID largo" — un async handler con manejo elaborado de
 *     errores (404 con recordatorio sobre acomodación vs ubicación,
 *     debug expandido, success-but-no-_id, network errors).
 *   - El componente es self-contained: solo necesita el form, los 2
 *     toggles de UI (buscando, unlocked) y URL del backend.
 *
 * REGLA DE NEGOCIO RELEVANTE
 *   - El ID largo es DERIVADO del ID corto via Stays. Por eso el flujo
 *     canónico es: admin pega ID corto → click "Obtener ID largo" →
 *     queda el largo sincronizado.
 *   - El candado solo se abre para casos edge (Stays ya no tiene el
 *     listing pero la propiedad sigue existiendo en AF, etc.) — por
 *     eso es OPT-IN explícito (modo "edición manual").
 *
 * Trim defensivo del ID corto: muy común que el admin pegue desde
 * Stays/Excel con whitespace invisible. Sin trim, encodeURIComponent
 * lo codifica como %20 y Stays no lo encuentra (fallaba silencioso).
 */

import React from "react";
import {
  Button,
  Form,
  Input,
  Space,
  Tag,
  Tooltip,
} from "antd";
import {
  LockOutlined,
  SearchOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import type { FormInstance } from "antd";
import { getModal, getMsg } from "../../../utils/antdStatic";

interface PropiedadIntegrationPanelProps {
  /** Form instance del Modal padre. */
  form: FormInstance;
  /** Loading del botón "Obtener ID largo" — el padre owna el state
   *  porque otros sitios podrían querer reaccionar a él. */
  buscandoIdLargo: boolean;
  setBuscandoIdLargo: React.Dispatch<React.SetStateAction<boolean>>;
  /** Estado del candado del ID largo: false = read-only (sync con
   *  Stays), true = editable manualmente. */
  idLargoUnlocked: boolean;
  setIdLargoUnlocked: React.Dispatch<React.SetStateAction<boolean>>;
  /** URL base del backend (incluye `/api`). El padre la pasa porque
   *  vive en apiClient — el panel hace fetch directo (no apiFetch)
   *  para poder leer el campo `debug` del body de error. */
  apiBaseUrl: string;
}

const PropiedadIntegrationPanel: React.FC<PropiedadIntegrationPanelProps> = ({
  form,
  buscandoIdLargo,
  setBuscandoIdLargo,
  idLargoUnlocked,
  setIdLargoUnlocked,
  apiBaseUrl,
}) => {
  return (
    <div className="flex-container">
      <Form.Item
        label="ID propiedad corto"
        tooltip="El ID corto (ej. JB01J) identifica la propiedad en Stays. Al obtenerlo, derivamos automáticamente el ID largo (ObjectId de MongoDB) necesario para los endpoints PMS."
      >
        <Space.Compact style={{ width: "100%", display: "flex" }}>
          <Form.Item name="idPropiedadCorto" noStyle>
            <Input style={{ flex: 1 }} />
          </Form.Item>

          <Button
            icon={<SearchOutlined />}
            loading={buscandoIdLargo}
            onClick={async () => {
              // Trim defensivo: si el admin pegó el ID corto con
              // whitespace invisible (muy común al copiar desde
              // Stays/Excel), encodeURIComponent lo codifica como
              // %20 y Stays no lo encuentra — fallaba silencioso.
              // Re-escribimos el campo trim para que el usuario
              // también lo vea limpio.
              const rawIdCorto = form.getFieldValue("idPropiedadCorto");
              const idCorto =
                typeof rawIdCorto === "string"
                  ? rawIdCorto.trim()
                  : rawIdCorto;
              if (!idCorto) {
                getMsg()?.warning("Ingrese primero el ID corto");
                return;
              }
              if (idCorto !== rawIdCorto) {
                form.setFieldsValue({ idPropiedadCorto: idCorto });
              }
              setBuscandoIdLargo(true);
              try {
                // Fetch directo (sin apiFetch) para poder leer el
                // campo `debug` del body de error y mostrárselo al
                // admin en un Modal — indica qué estrategias del
                // backend se intentaron y qué status/mensaje devolvió
                // Stays en cada una. Fundamental para diagnosticar
                // credenciales rechazadas vs. listing inexistente vs.
                // API cambiada.
                // La auth viaja por cookie HttpOnly (`credentials:
                // "include"` lo agrega el interceptor global). Round
                // 2026-04-26 fase 6 (P1-B).
                const res = await fetch(
                  `${apiBaseUrl}/stays/listing-long-id/${encodeURIComponent(
                    idCorto,
                  )}`,
                );
                const json = await res.json().catch(() => null);
                // Log solo en desarrollo: el body de Stays puede
                // incluir metadata de listing que no queremos filtrar
                // en DevTools de producción.
                if (process.env.NODE_ENV !== "production") {
                  // eslint-disable-next-line no-console
                  console.log(
                    "[Buscar ID largo] request",
                    idCorto,
                    "→ status",
                    res.status,
                    "body",
                    json,
                  );
                }
                if (!res.ok || !json?.success) {
                  const debugText = json?.debug
                    ? JSON.stringify(json.debug, null, 2)
                    : null;
                  const notFound = res.status === 404;
                  getModal()?.error({
                    title: notFound
                      ? `No encontramos el anuncio "${idCorto}" en Stays`
                      : `No se pudo obtener el ID largo (HTTP ${res.status})`,
                    width: 600,
                    content: (
                      <div>
                        <p style={{ marginTop: 0 }}>
                          {json?.message ??
                            "Error desconocido al contactar Stays."}
                        </p>
                        {notFound ? (
                          // Recordatorio crítico: en Stays hay
                          // "ubicaciones" (lugares) Y "acomodaciones"
                          // (anuncios reales). Sin acomodación
                          // publicada, la API no expone el listing.
                          // Ver docs/modulo-stays.md.
                          <div
                            style={{
                              background: "#fffbeb",
                              border: "1px solid #fde68a",
                              borderRadius: 8,
                              padding: "10px 12px",
                              margin: "10px 0",
                              fontSize: 13,
                              lineHeight: 1.5,
                              color: "#92400e",
                            }}
                          >
                            <p style={{ margin: 0, fontWeight: 600 }}>
                              Recordatorio
                            </p>
                            <p style={{ margin: "6px 0 0" }}>
                              En Stays tienes que crear una{" "}
                              <strong>acomodación</strong> — el anuncio
                              real del departamento, casa, cabaña, etc.
                              Una <em>ubicación</em> sola no alcanza:
                              sin anuncio publicado, Stays no expone el
                              listing en la API y por eso no aparece
                              acá.
                            </p>
                            <p
                              style={{
                                margin: "6px 0 0",
                                fontSize: 12,
                              }}
                            >
                              Revisá que el anuncio exista, que el ID
                              corto sea exactamente <code>{idCorto}</code>{" "}
                              y reintentá.
                            </p>
                          </div>
                        ) : null}
                        {debugText ? (
                          <>
                            <p style={{ marginBottom: 4 }}>
                              <strong>
                                Diagnóstico (estrategias probadas):
                              </strong>
                            </p>
                            <pre
                              style={{
                                background: "#f3f4f6",
                                padding: 10,
                                borderRadius: 6,
                                fontSize: 11,
                                overflowX: "auto",
                                maxHeight: 220,
                              }}
                            >
                              {debugText}
                            </pre>
                            <p
                              style={{
                                marginTop: 6,
                                fontSize: 12,
                                color: "#6b7280",
                              }}
                            >
                              Copiá este bloque si reportás el problema.
                            </p>
                          </>
                        ) : null}
                      </div>
                    ),
                  });
                  return;
                }
                const idLargo = json?.data?._id ?? null;
                if (idLargo) {
                  form.setFieldsValue({ idPropiedadLargo: idLargo });
                  getMsg()?.success("ID largo encontrado");
                } else {
                  // Antes era un `message.error` (toast ~3s) que el
                  // admin se perdía fácil — escenario típico: backend
                  // viejo devolvía {success:true, data:{_id: undefined}}
                  // cuando Stays matcheaba por _idext pero el objeto
                  // listing no traía `_id` al top. Un Modal sí se ve.
                  getModal()?.error({
                    title: "Stays respondió OK pero sin _id",
                    width: 560,
                    content: (
                      <div>
                        <p style={{ marginTop: 0 }}>
                          El backend respondió{" "}
                          <code>success:true</code> pero el payload no
                          trae <code>data._id</code>. Esto pasa cuando
                          el listing existe en Stays pero la estrategia
                          que lo encontró devolvió un resumen sin el
                          ObjectId (o el backend deployado es anterior
                          al fix del 23-abr-2026).
                        </p>
                        <p style={{ marginBottom: 4 }}>
                          <strong>Respuesta recibida:</strong>
                        </p>
                        <pre
                          style={{
                            background: "#f3f4f6",
                            padding: 10,
                            borderRadius: 6,
                            fontSize: 11,
                            overflowX: "auto",
                            maxHeight: 220,
                          }}
                        >
                          {JSON.stringify(json, null, 2)}
                        </pre>
                      </div>
                    ),
                  });
                }
              } catch (e: any) {
                getModal()?.error({
                  title: "Error de red al contactar Stays",
                  content:
                    e?.message ??
                    "Error al buscar ID largo en Stays",
                });
              } finally {
                setBuscandoIdLargo(false);
              }
            }}
          >
            Obtener ID largo
          </Button>
        </Space.Compact>
      </Form.Item>

      {/*
       * ID largo — read-only por default (modo "auto desde Stays").
       * Candado lateral permite destrabar a "edición manual" para
       * casos edge (admin necesita pegar un ObjectId que Stays no
       * devolvió, propiedad migrada, etc.).
       */}
      <Form.Item
        label={
          <span>
            ID propiedad largo{" "}
            <Tag
              color={idLargoUnlocked ? "orange" : "default"}
              style={{ marginLeft: 6 }}
            >
              {idLargoUnlocked ? "edición manual" : "auto desde Stays"}
            </Tag>
          </span>
        }
        name="idPropiedadLargo"
        tooltip={
          idLargoUnlocked
            ? "Editable manualmente. Cerrá el candado para volver al modo sincronizado."
            : "Se completa automáticamente al presionar 'Obtener ID largo' arriba. Abrí el candado para editar manualmente."
        }
      >
        <Input
          readOnly={!idLargoUnlocked}
          placeholder={
            idLargoUnlocked
              ? "Ingrese el ObjectId de Stays"
              : "Se completa al obtener desde el ID corto"
          }
          suffix={
            <Tooltip
              title={
                idLargoUnlocked
                  ? "Bloquear (modo sincronizado)"
                  : "Destrabar para editar manualmente"
              }
            >
              <Button
                type="text"
                size="small"
                icon={
                  idLargoUnlocked ? <UnlockOutlined /> : <LockOutlined />
                }
                onClick={() => setIdLargoUnlocked((v) => !v)}
              />
            </Tooltip>
          }
        />
      </Form.Item>
    </div>
  );
};

export default PropiedadIntegrationPanel;
